/**
 * Slug used as a classify_email / classify_document_v2 category id when
 * categories are defined inline (name + description). Enum-sourced
 * categories keep the CoreEnumOption UUID instead.
 *
 * The same function must run in the socket generator (canvas + save path)
 * and in the handler that writes payload keys, or a renamed category
 * would mint a socket the handler never fills.
 */
export function slugifyClassificationName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

const EMPTY_SLUG_FALLBACK = "category";

/**
 * Unique category ids for a list of inline names, in order.
 *
 * `slugifyClassificationName` maps distinct labels onto the same key
 * (`"A-B"` and `"A B"` both become `a_b`; punctuation-only names become
 * `""`). The generator and the handler both run this so each configured
 * category still gets its own socket and payload key.
 */
export function uniqueClassificationSlugIds(
  names: readonly string[],
): string[] {
  const used = new Set<string>();
  const ids: string[] = [];
  for (const name of names) {
    const slug = slugifyClassificationName(name);
    const base = slug.length > 0 ? slug : EMPTY_SLUG_FALLBACK;
    let id = base;
    let suffix = 2;
    while (used.has(id)) {
      id = `${base}_${suffix}`;
      suffix += 1;
    }
    used.add(id);
    ids.push(id);
  }
  return ids;
}
